function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { useState } from 'react';
import isEqual from 'lodash/isEqual';
import produce from 'immer';
import { SelectTheme } from 'common/components/select/constants';
import { SelectReact } from 'common/components/select';
import { TooltipReact } from 'common/components/tooltip';
import { Bucket, BucketSizeReact } from 'common/widgets/bucket-sizes';
import { SvgIconReact } from 'common/components/svg-icon';
import { Directions } from 'common/components/drop-menu/types';
import { ButtonSize } from 'common/components/button/constants';
import { DisplaySize, Placement } from 'common/components/tooltip/types';
import { TimeUnit } from 'common/widgets/date-range-picker/models/types';
import { useTranslation } from 'common/i18n/hooks';
import onImg from 'common/assets/images/retention-criteria/on.webp';
import onOrAfterImg from 'common/assets/images/retention-criteria/on_or_after.webp';
import onOrBeforeImg from 'common/assets/images/retention-criteria/on_or_before.webp';
import streakImg from 'common/assets/images/retention-criteria/streak.webp';
import { ChartType, MetricType, RetentionAlignmentType, RetentionUnboundedModeType } from 'common/types/reports/bookmark';
import { getCurrentMoment } from 'common/util/date';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { SectionItem } from 'common/components/items-menu/types';
import { MetricEntry } from 'common/query-builder/query-entry-section/types';
import { SwitchChangeEvent, SwitchEl } from 'common/components/switch';
import { MetricBehavior } from 'common/report/insights/util/types';
import type { RowProps } from '../catalog';
import styles from './index.module.styl';
export const CUSTOM_RETENTION_UNIT = "custom";
const commonTooltipAttrs = {
  size: DisplaySize.Small
};

/**
 * Renders and manages the retention criteria portion of a retention behavior
 * It manages things like on, on or after retention, retention unit selection, etc...
 */
export function RetentionCriteria(props: RowProps) {
  var _props$entry$behavior;
  const {
    t,
    formatDate
  } = useTranslation();
  const currentProject = useCurrentProject();
  const [isCustomBucketOpen, setIsCustomBucketOpen] = useState(false);
  const unboundedMode = props.entry.behavior.retentionUnboundedMode;
  const retentionUnit = props.entry.behavior.retentionUnit;
  const bucketSizes = (_props$entry$behavior = props.entry.behavior.retentionCustomBucketSizes) !== null && _props$entry$behavior !== void 0 ? _props$entry$behavior : [];
  const retentionAlignmentType = props.entry.behavior.retentionAlignmentType;
  const disableCohortize = props.entry.behavior.disableCohortize;
  const chartType = props.currentChartType;
  const bucketLength = bucketSizes.length;
  const selectedRetentionUnitValue = bucketLength !== 0 ? CUSTOM_RETENTION_UNIT : retentionUnit;
  const updateBehavior = (updates: Partial<MetricBehavior>) => {
    props.updateEntry(produce(props.entry, draft => {
      draft.hasUnsavedChanges = true;
      Object.assign(draft.behavior, updates);
    }));
  };

  /** Sets the retention count type to On,, On or After, Strike, etc... */
  const handleChangeCountingMethodSelector = (value: RetentionUnboundedModeType) => updateBehavior({
    retentionUnboundedMode: value
  });

  /** Sets the retention intervals */
  const handleRetentionUnitChanged = (_ref: {
    retentionUnit?: TimeUnit;
    bucketSizes?: number[];
  }) => {
    let {
      retentionUnit: updatedRetentionUnit,
      bucketSizes: updatedBucketSizes
    }: {
      retentionUnit?: TimeUnit;
      bucketSizes?: number[];
    } = _ref;
    const behaviorModifier = (entry: MetricEntry) => produce(entry, draft => {
      draft.behavior.retentionUnit = updatedRetentionUnit !== null && updatedRetentionUnit !== void 0 ? updatedRetentionUnit : draft.behavior.retentionUnit;
      draft.behavior.retentionCustomBucketSizes = updatedBucketSizes !== null && updatedBucketSizes !== void 0 ? updatedBucketSizes : [];
      draft.behavior.hasUnsavedChanges = true;

      // if retention unit changed, set bucket index to 0
      if (!isEqual(draft.behavior.retentionUnit, updatedRetentionUnit)) {
        draft.measurement.retentionBucketIndex = 0;
      }
    });
    // TODO(CC): When the chart type is retention curve, changing the retention unit or bucket sizes
    // of a retention metric changes the retention units of all the other existing retention metrics.
    // Tune this later when we find a better solution to handle this.
    if (props.currentChartType === ChartType.RetentionCurve) {
      const entries = props.getEntries() as MetricEntry[];
      entries.forEach((entry, index) => {
        if (props.entry.behavior.type === MetricType.Retention) {
          props.updateEntry(behaviorModifier(entry), {
            index
          });
        }
      });
    }

    // updates rendered retention metric
    props.updateEntry(behaviorModifier(props.entry));
  };
  const isCalendarAlignment = (alignmentType: RetentionAlignmentType) => {
    return alignmentType !== RetentionAlignmentType.Birth;
  };
  const getBucketLabel = (retentionBucketSizes: number[]) => {
    let previousBucket = 0;
    const label = retentionBucketSizes.map(bucket => {
      const currentBucket = previousBucket + bucket;
      const retLabel = bucket === 1 ? "".concat(currentBucket - 1) : "".concat(previousBucket, "-").concat(currentBucket - 1);
      previousBucket = currentBucket;
      return retLabel;
    }).join(" / ");
    return label;
  };
  const getLabel = (retentionBucketSizes: number[], bucketUnit: TimeUnit, alignmentType: RetentionAlignmentType) => {
    const map = isCalendarAlignment(alignmentType) ? {
      [TimeUnit.Day]: t("query-builder.retention.interval.label.calendar-day"),
      [TimeUnit.Week]: t("query-builder.retention.interval.label.calendar-week"),
      [TimeUnit.Month]: t("query-builder.retention.interval.label.calendar-month")
    } : {
      [TimeUnit.Day]: t("query-builder.retention.interval.label.day"),
      [TimeUnit.Week]: t("query-builder.retention.interval.label.week"),
      [TimeUnit.Month]: t("query-builder.retention.interval.label.month")
    };
    const label = getBucketLabel(retentionBucketSizes);
    const unit = map[bucketUnit];
    return "".concat(unit, " ").concat(label);
  };
  const getUnitSelectorTooltip = () => bucketSizes && bucketSizes.length ? _objectSpread({
    text: "Returning users on ".concat(retentionUnit, "s: ").concat(getBucketLabel(bucketSizes))
  }, commonTooltipAttrs) : commonTooltipAttrs;
  const getRetentionUnboundedOptions = (): Array<SectionItem<RetentionUnboundedModeType>> => [{
    label: () => t("query-builder.retention.criteria.on-or-after"),
    onSelect: () => handleChangeCountingMethodSelector("carry_back"),
    isSelected: unboundedMode === "carry_back",
    tooltip: _objectSpread({
      text: () => t("query-builder.retention.criteria.tooltip.on-or-after"),
      'image-src': onOrAfterImg
    }, commonTooltipAttrs)
  }, {
    label: () => t("query-builder.retention.criteria.on"),
    isSelected: !unboundedMode || unboundedMode === "none",
    onSelect: () => handleChangeCountingMethodSelector("none"),
    tooltip: _objectSpread({
      text: () => t("query-builder.retention.criteria.tooltip.on"),
      'image-src': onImg
    }, commonTooltipAttrs)
  }, {
    label: () => t("query-builder.retention.criteria.on-or-before"),
    onSelect: () => handleChangeCountingMethodSelector("carry_forward"),
    isSelected: unboundedMode === "carry_forward",
    tooltip: _objectSpread({
      text: () => t("query-builder.retention.criteria.tooltip.on-or-before"),
      'image-src': onOrBeforeImg
    }, commonTooltipAttrs)
  }, {
    label: () => t("query-builder.retention.criteria.consecutive-forward"),
    onSelect: () => handleChangeCountingMethodSelector("consecutive_forward"),
    isSelected: unboundedMode === "consecutive_forward",
    tooltip: _objectSpread({
      text: () => t("query-builder.retention.criteria.tooltip.consecutive-forward"),
      'image-src': streakImg
    }, commonTooltipAttrs)
  }];
  const getAdvancedMenuOptions = (): SectionItem[] => {
    var _currentProject$get;
    const timezone = (_currentProject$get = currentProject.get()) === null || _currentProject$get === void 0 || (_currentProject$get = _currentProject$get.value) === null || _currentProject$get === void 0 ? void 0 : _currentProject$get.timezone;
    const today = formatDate(getCurrentMoment({
      timezone
    }), {
      month: "short",
      day: "numeric"
    });
    const tomorrow = formatDate(getCurrentMoment({
      timezone
    }).add(1, "days"), {
      month: "short",
      day: "numeric"
    });
    const items: SectionItem[] = [{
      label: t("retention.group-by-date"),
      tooltip: {
        placement: Placement.Top,
        'max-width': "185px",
        text: t("retention.group-by-date-tooltip", {
          today,
          tomorrow
        })
      },
      actionsRenderer: () => SwitchEl({
        attrs: {
          on: retentionAlignmentType === RetentionAlignmentType.IntervalStart
        },
        on: {
          change: (ev: SwitchChangeEvent) => updateBehavior({
            retentionAlignmentType: ev.detail.on ? RetentionAlignmentType.IntervalStart : RetentionAlignmentType.Birth
          })
        }
      })
    }];
    if (chartType !== ChartType.Line) {
      items.push({
        label: t("retention.cohortize"),
        tooltip: {
          placement: Placement.Top,
          'max-width': "185px",
          text: t("retention.cohortize-tooltip")
        },
        actionsRenderer: () => SwitchEl({
          attrs: {
            on: disableCohortize !== true
          },
          on: {
            change: (ev: SwitchChangeEvent) => updateBehavior({
              disableCohortize: !ev.detail.on
            })
          }
        })
      });
    }
    return items;
  };
  return <div className={styles.retentionCriteria} data-sentry-component="RetentionCriteria" data-sentry-source-file="retention-criteria.tsx">
      <div className={styles.iconWrapper}>
        <SvgIconReact icon='time' data-sentry-element="SvgIconReact" data-sentry-source-file="retention-criteria.tsx" />
      </div>

      {/* selects retention on, on or after, on or before, streak */}
      <SelectReact className={"".concat(styles.select, " ").concat(styles.methodSelect)} menuWidth={-1} theme={SelectTheme.Tertiary} size={ButtonSize.Small} sections={[{
      items: getRetentionUnboundedOptions()
    }]} data-sentry-element="SelectReact" data-sentry-source-file="retention-criteria.tsx" />

      {/* retention bucket unit selection, on each day, week, month, etc... */}
      <SelectReact className={styles.select} elref='retUnitSelector' label={getLabel(bucketSizes, retentionUnit, retentionAlignmentType)} theme={SelectTheme.Tertiary} size={ButtonSize.Small} menuWidth={-1} tooltip={getUnitSelectorTooltip()} sections={[{
      items: [{
        value: TimeUnit.Day,
        onSelect: () => handleRetentionUnitChanged({
          retentionUnit: TimeUnit.Day
        }),
        isSelected: selectedRetentionUnitValue === TimeUnit.Day,
        label: t("query-builder.retention.interval.day"),
        tooltip: _objectSpread({
          text: t("query-builder.retention.interval.tooltip.day")
        }, commonTooltipAttrs)
      }, {
        value: TimeUnit.Week,
        onSelect: () => handleRetentionUnitChanged({
          retentionUnit: TimeUnit.Week
        }),
        isSelected: selectedRetentionUnitValue === TimeUnit.Week,
        label: t("query-builder.retention.interval.week"),
        tooltip: _objectSpread({
          text: t("query-builder.retention.interval.tooltip.week")
        }, commonTooltipAttrs)
      }, {
        value: TimeUnit.Month,
        onSelect: () => handleRetentionUnitChanged({
          retentionUnit: TimeUnit.Month
        }),
        isSelected: selectedRetentionUnitValue === TimeUnit.Month,
        label: t("query-builder.retention.interval.month"),
        tooltip: _objectSpread({
          text: t("query-builder.retention.interval.tooltip.month")
        }, commonTooltipAttrs)
      }, {
        value: CUSTOM_RETENTION_UNIT,
        isSelected: selectedRetentionUnitValue === CUSTOM_RETENTION_UNIT,
        label: t("query-builder.retention.interval.custom"),
        onSelect: () => setIsCustomBucketOpen(true),
        tooltip: _objectSpread({
          text: t("query-builder.retention.interval.tooltip.custom")
        }, commonTooltipAttrs)
      }]
    }]} data-sentry-element="SelectReact" data-sentry-source-file="retention-criteria.tsx" />

      {/* retention custom bucket selector */}
      {isCustomBucketOpen && <TooltipReact text={"".concat(t("query-builder.retention.interval.custom-menu.returning-users"), ": ").concat(bucketSizes)} />}
      {isCustomBucketOpen && <BucketSizeReact className={styles.bucketSizes}
    // @ts-ignore
    header={t("query-builder.retention.interval.custom-menu.title")} bucketUnit={retentionUnit} bucketSizes={bucketSizes} onApply={(ev: CustomEvent<{
      bucketUnit: TimeUnit;
      buckets: Bucket[];
    }>) => {
      setIsCustomBucketOpen(false);
      handleRetentionUnitChanged({
        retentionUnit: ev.detail.bucketUnit,
        bucketSizes: ev.detail.buckets.map(bucket => bucket.interval)
      });
    }} onCancel={() => setIsCustomBucketOpen(false)} />}

      {/* advanced dropdown to show options like cohortize or group by dates */}
      <SelectReact className={"".concat(styles.select, " ").concat(styles.advancedMenu)} slot='actions' theme={SelectTheme.Tertiary} size={ButtonSize.Small} alwaysShowCaret selectedOption={t("query-builder.advanced")} menuAlignment={Directions.Right} tooltip={{
      placement: Placement.Right
    }} experimentalPositioning menuWidth={-1} sections={[{
      items: getAdvancedMenuOptions()
    }]} data-sentry-element="SelectReact" data-sentry-source-file="retention-criteria.tsx" />
    </div>;
}